import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import TrustSignals from '@/components/TrustSignals';
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";

const PriceForm = () => {
  const { t } = useTranslation(['common', 'contact']);
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';
  const filloutContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load Fillout script dynamically
    const script = document.createElement('script');
    script.src = 'https://server.fillout.com/embed/v1/';
    script.async = true;
    
    script.onload = () => {
      // The script will automatically initialize the form based on data attributes
    };
    
    script.onerror = () => {
      console.error('Failed to load Fillout script');
    };
    
    document.head.appendChild(script);

    // Cleanup: remove script when component unmounts
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Auto-scroll to form if coming from another page
  useEffect(() => {
    const shouldScroll = searchParams.get('scroll') === 'form';
    
    if (shouldScroll && filloutContainerRef.current) {
      // Small delay to ensure the form is rendered
      const timer = setTimeout(() => {
        filloutContainerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        
        // Clean up URL by removing scroll parameter
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('scroll');
        setSearchParams(newSearchParams, { replace: true });
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  return (
    <>
      <SEOHead 
        page="price-form"
      />
      <div className="min-h-screen bg-gradient-subtle">
        <Header />
        
        <main className="container mx-auto px-4 py-8">
          <Breadcrumb />
          
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                {t('buttons.scheduleCall')}
              </h1>
              <p className="text-xl text-muted-foreground mb-6">
                {t('buttons.scheduleConsultation')}
              </p>
              
              {/* Risk Reversal Message */}
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
                <div className="text-center">
                  <h3 className="font-semibold text-foreground mb-2">100% Risk-Free Consultation</h3>
                  <p className="text-sm text-muted-foreground">
                    No obligation • Free consultation • Money-back guarantee if not satisfied
                  </p>
                </div>
              </div>
            </div>

            {/* Fillout Embed Container */}
            <div className="bg-card rounded-lg shadow-elegant border p-6">
              <div 
                ref={filloutContainerRef}
                style={{width: '100%', height: '500px'}} 
                data-fillout-id="gJVLhpRvrFus" 
                data-fillout-embed-type="standard" 
                data-fillout-inherit-parameters 
                data-fillout-dynamic-resize 
                data-fillout-domain="form.danmag.com"
              ></div>
            </div>
            
            {/* Trust Signals Below Form */}
            <div className="mt-8">
              <TrustSignals />
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PriceForm;